import { useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading, formatBytes } from "../../components/Feedback";
import { Badge, Btn, ConfirmDialog } from "../../components/Shared";
import { useApi } from "../../useApi";

// The versions of one file (GET /api/files/{id}/versions, newest first). A replace keeps the old
// bytes as a superseded version; any reader downloads them, the dataset's editors restore one (a
// replace with a copy of it: a new version, the old one untouched), and an admin purges a
// superseded one unless a frozen design version was built from it (409, shown as the error).
// knowledge/reference/storage-and-s3.md, "Versions".

const when = iso => (iso ? new Date(iso).toLocaleString() : "");

export function versionState(v) {
  if (v.purged_at) return { label: "purged", color: "red" };
  if (v.current) return { label: "current", color: "green" };
  if (v.deleted_at) return { label: "deleted", color: "amber" };
  return { label: "superseded", color: "gray" };
}

export default function FileVersions({ file, canEdit, isAdmin, onRestored }) {
  const { data, error, loading, reload } = useApi(`/files/${file.id}/versions`);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [purging, setPurging] = useState(null);

  const download = v => api.download(`/files/${file.id}/versions/${v.version}/download`,
                                     v.filename).catch(setActionError);
  const restore = async v => {
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/files/${file.id}/versions/${v.version}/restore`);
      reload();
      onRestored && onRestored();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };
  const purge = async v => {
    setPurging(null);
    setActionError(null);
    try {
      await api.post(`/admin/file-versions/${v.id}/purge`);
      reload();
    } catch (err) {
      setActionError(err);
    }
  };

  if (loading && !data) return <Loading label="Loading versions…" />;
  return (
    <div style={{ padding: "6px 0" }}>
      <ErrorNote error={error || actionError} />
      {data && (
        <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <caption style={{ position: "absolute", left: -9999 }}>Versions of {file.filename}</caption>
          <thead>
            <tr style={{ textAlign: "left", color: "#6b7280", fontSize: 11, textTransform: "uppercase" }}>
              <th scope="col" style={{ padding: "4px 8px" }}>Version</th>
              <th scope="col" style={{ padding: "4px 8px" }}>Uploaded</th>
              <th scope="col" style={{ padding: "4px 8px" }}>Size</th>
              <th scope="col" style={{ padding: "4px 8px" }}>State</th>
              <th scope="col" style={{ padding: "4px 8px" }}><span style={{ position: "absolute", left: -9999 }}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {data.map(v => {
              const state = versionState(v);
              return (
                <tr key={v.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                  <td data-label="Version" style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>
                    v{v.version}{v.restored_from ? ` (restored from v${v.restored_from})` : ""}
                  </td>
                  <td data-label="Uploaded" style={{ padding: "4px 8px" }}>{when(v.created_at)}{v.uploaded_by ? ` by ${v.uploaded_by}` : ""}</td>
                  <td data-label="Size" style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{formatBytes(v.size)}</td>
                  <td data-label="State" style={{ padding: "4px 8px" }}>
                    <Badge color={state.color}>{state.label}</Badge>
                    {v.superseded_at && !v.purged_at && (
                      <span style={{ color: "#6b7280" }}> {when(v.superseded_at)}{v.superseded_by ? ` by ${v.superseded_by}` : ""}</span>
                    )}
                    {v.referenced_by.length > 0 && (
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        Frozen in design version {v.referenced_by.join(", ")}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "4px 8px", whiteSpace: "nowrap", textAlign: "right" }}>
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      {!v.purged_at && (
                        <Btn variant="secondary" onClick={() => download(v)}>Download v{v.version}</Btn>
                      )}
                      {canEdit && !v.current && !v.purged_at && (
                        <Btn variant="secondary" disabled={busy} onClick={() => restore(v)}>Restore v{v.version}</Btn>
                      )}
                      {isAdmin && !v.current && !v.purged_at && (
                        <Btn variant="danger" onClick={() => setPurging(v)}>Purge v{v.version}</Btn>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {purging && (
        <ConfirmDialog
          title="Purge file version"
          confirmWord={`v${purging.version}`}
          confirmLabel="Purge"
          onConfirm={() => purge(purging)}
          onClose={() => setPurging(null)}
        >
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            Remove the bytes of <strong>{purging.filename}</strong> version {purging.version} for good?
            The record of the version stays. A version a frozen design version was built from cannot be purged.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
